import { h, Fragment } from "preact";
import { useState } from "preact/hooks";

import { JiraAttachmentDetails } from "../../../../foundation/model/JiraAttachment";
import Link from "../../../../foundation/router/Link";
import classname from "../../../../foundation/utils/classname";
import { getDateString, formatTime } from "../../../../foundation/utils/date";
import MaybeImage from "../../../MaybeImage";
import JiraStatusTag from "../JiraStatusTag";

import styles from "./JiraIssueCard.module.css";

type Props = {
    issueKey: string;
    details: JiraAttachmentDetails;
    href: string | undefined;
    onClick?: (openInJira?: boolean) => void;
};

function JiraIssueCard({
    issueKey,
    details,
    href,
    onClick
}: Props) {
    const [showExtendedDate, setShowExtendedDate] = useState(false);

    function handleDateMouseEnter() {
        setShowExtendedDate(true);
    }

    function handleDateMouseLeave() {
        setShowExtendedDate(false);
    }

    function createJiraItemClickHandler(openInJira: boolean) {
        return () => onClick?.(openInJira);
    }

    return (
        <div class={classname(styles.jiraIssueCard, "body-1")}>
            <div class={styles.issueCardHeader}>
                <div class={styles.issueKeyLine}>
                    {details.parent && (
                        <>
                            <span class={classname(styles.parentIssueKey, "body-1", "medium")}>
                                {details.parent.issueKey}
                            </span>
                            <span class={classname(styles.parentIssueSeparator, "body-1", "medium")}>
                                /
                            </span>
                        </>
                    )}
                    <MaybeImage class={styles.issueIcon} src={details.issueType.iconUrl}/>
                    <Link
                        to={href}
                        class={classname(styles.issueKey, "body-1", "medium")}
                        isExternal
                        onClick={createJiraItemClickHandler(false)}>
                        {issueKey}
                    </Link>
                </div>
                <JiraStatusTag status={details.status}/>
            </div>
            <div class={classname(styles.jiraIssueSummary, "heading-3", "medium")}>
                {details.summary}
            </div>
            <div class={styles.jiraIssueCardLine}>
                <span class={styles.jiraIssueCardLineKey}>
                    Assignee
                </span>
                {details.assignee && <MaybeImage class={styles.assigneeAvatar} src={details.assignee.avatarUrl}/>}
                <span class={styles.jiraIssueCardLineValue}>
                    {details.assignee ? details.assignee.displayName : "Unassigned"}
                </span>
            </div>
            {details.priority && (
                <div class={styles.jiraIssueCardLine}>
                    <span class={styles.jiraIssueCardLineKey}>
                        Priority
                    </span>
                    <MaybeImage class={styles.priorityIcon} src={details.priority.iconUrl}/>
                    <span class={classname(styles.jiraIssueCardLineValue, "ellipsis")}>
                        {details.priority.name}
                    </span>
                </div>
            )}
            {details.dueDate && (
                <div class={styles.jiraIssueCardLine}>
                    <span class={styles.jiraIssueCardLineKey}>
                        Due Date
                    </span>
                    <span class={styles.jiraIssueCardLineValue}>
                        {getDateString(details.dueDate)}
                    </span>
                </div>
            )}
            {details.updated && (
                <div class={styles.lastUpdated} onMouseEnter={handleDateMouseEnter} onMouseLeave={handleDateMouseLeave}>
                    {showExtendedDate ? (
                        formatTime(details.updated, {
                            dateStyle: "medium",
                            timeStyle: "short"
                        })
                    ) : (
                        <>
                            Updated <zpl-time-ago format="microWithAgo" datetime={details.updated}/>
                        </>
                    )}
                </div>
            )}
            <div class={styles.openInJira}>
                <Link
                    to={href}
                    class={classname(styles.link, "body-1", "medium")}
                    isExternal
                    onClick={createJiraItemClickHandler(true)}>
                    Open in Jira
                </Link>
            </div>
        </div>
    );
}

export default JiraIssueCard;
